import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  FormControlLabel,
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  Dashboard,
  Warning,
  Schedule,
  Assignment,
  Build,
  CheckCircle,
  TrendingUp,
  Settings,
  Refresh,
  FilterList,
  CalendarToday,
  ListAlt,
  PriorityHigh,
  Engineering,
} from '@mui/icons-material';

import { useSiteContext } from '../../hooks/useSiteContext';
import MaintenancePackageWorkflow from '../../components/Maintenance/MaintenancePackageWorkflow';
import HierarchicalOverdueTaskManager from '../../components/Maintenance/HierarchicalOverdueTaskManager';
import { maintenanceService } from '../../services/maintenanceService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Type for calendar events
interface CalendarEvent {
  type: 'schedule' | 'workorder';
  title: string;
  equipment?: string;
  status?: string;
  priority?: string;
  isOverdue?: boolean;
  data: any;
}

// Calendar component for maintenance scheduling
const MaintenanceCalendar: React.FC<{
  schedules: any[];
  workOrders: any[];
  currentDate: Date;
  onDateChange: (date: Date) => void;
}> = ({ schedules, workOrders, currentDate, onDateChange }) => {
      const monthStart = dayjs(currentDate).startOf('month');
    const monthEnd = dayjs(currentDate).endOf('month');
    const monthDays = [];
    let current = monthStart;
    while (current.isBefore(monthEnd) || current.isSame(monthEnd, 'day')) {
      monthDays.push(current.toDate());
      current = current.add(1, 'day');
    }

  // Get events for a specific date
  const getEventsForDate = (date: Date): CalendarEvent[] => {
          const dateStr = dayjs(date).format('YYYY-MM-DD');
    const events: CalendarEvent[] = [];

    // Add overdue/upcoming schedules
    schedules.forEach(schedule => {
              if (schedule.next_due_date && dayjs(schedule.next_due_date).isSame(date, 'day')) {
        events.push({
          type: 'schedule',
          title: schedule.task_name,
          equipment: schedule.equipment_name,
          isOverdue: new Date(schedule.next_due_date) < new Date(),
          data: schedule
        });
      }
    });

    // Add work orders
    workOrders.forEach(workOrder => {
              if (workOrder.planned_start_date && dayjs(workOrder.planned_start_date).isSame(date, 'day')) {
        events.push({
          type: 'workorder',
          title: workOrder.work_order_number || 'Work Order',
          status: workOrder.status,
          priority: workOrder.priority,
          data: workOrder
        });
      }
    });

    return events;
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {dayjs(currentDate).format('MMMM YYYY')}
          </Typography>
          <Box>
            <Button onClick={() => onDateChange(dayjs(currentDate).add(-30, 'day').toDate())}>Previous</Button>
            <Button onClick={() => onDateChange(new Date())}>Today</Button>
            <Button onClick={() => onDateChange(dayjs(currentDate).add(30, 'day').toDate())}>Next</Button>
          </Box>
        </Box>

        <Grid container spacing={1}>
          {/* Days of week header */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <Grid item xs={12/7} key={day}>
              <Typography variant="subtitle2" textAlign="center" sx={{ py: 1, fontWeight: 'bold' }}>
                {day}
              </Typography>
            </Grid>
          ))}

          {/* Calendar days */}
          {monthDays.map(date => {
            const events = getEventsForDate(date);
            const hasOverdue = events.some(e => e.type === 'schedule' && e.isOverdue);
            
            return (
              <Grid item xs={12/7} key={date.toString()}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    minHeight: 100,
                    backgroundColor: dayjs().isSame(date, 'day') ? 'primary.light' : 'inherit',
                    border: hasOverdue ? '2px solid red' : '1px solid grey.300',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'grey.50' }
                  }}
                  onClick={() => onDateChange(date)}
                >
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="body2" fontWeight={dayjs().isSame(date, 'day') ? 'bold' : 'normal'}>
                      {dayjs(date).format('D')}
                    </Typography>
                    {events.map((event, idx) => (
                      <Tooltip key={idx} title={`${event.title} - ${event.equipment || event.status}`}>
                        <Chip
                          label={event.title.substring(0, 10) + (event.title.length > 10 ? '...' : '')}
                          size="small"
                          color={
                            event.type === 'schedule' ? (event.isOverdue ? 'error' : 'warning') :
                            event.status === 'completed' ? 'success' : 'info'
                          }
                          sx={{ fontSize: '0.6rem', height: 16, mb: 0.5, display: 'block' }}
                        />
                      </Tooltip>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`maintenance-tabpanel-${index}`}
      aria-labelledby={`maintenance-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface DashboardStats {
  total_packages: number;
  overdue_packages: number;
  due_this_week: number;
  in_progress_packages: number;
  overdue_assignments: number;
  total_assignments: number;
  completion_rate: number;
  avg_completion_time: number;
}

const EnhancedMaintenanceDashboard: React.FC = () => {
  const { currentSite } = useSiteContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    total_packages: 0,
    overdue_packages: 0,
    due_this_week: 0,
    in_progress_packages: 0,
    overdue_assignments: 0,
    total_assignments: 0,
    completion_rate: 0,
    avg_completion_time: 0,
  });

  // View options
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState<null | HTMLElement>(null);

  // Calendar and detailed data
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [dashboardData, setDashboardData] = useState<any>(null);

    // Load dashboard statistics
  const loadDashboardStats = async () => {
    if (!currentSite.siteId) return;

    try {
      setLoading(true);
      setError(null);

      // Load comprehensive dashboard data
      const statsResponse = await maintenanceService.dashboard.getDashboardData(
        currentSite.siteId.toString()
      );

      // ALSO load maintenance tasks for calendar display
      const tasksParams = new URLSearchParams();
      tasksParams.append('site', currentSite.siteId.toString());
      const tasksResponse = await fetch(`/api/v1/maintenance/tasks/?${tasksParams}`);
      const tasksData = await tasksResponse.json();

      // Combine old schedules with new maintenance tasks for comprehensive calendar view
      const allMaintenanceItems = [
        ...(statsResponse.overdue_schedules || []),
        ...(statsResponse.upcoming_schedules || []),
        ...(tasksData.results || [])
      ];

      // Remove duplicates and combine into unified format
      const uniqueItems = allMaintenanceItems.reduce((acc, item) => {
        const key = `${item.id}_${item.task_name || item.name}`;
        if (!acc[key]) {
          acc[key] = {
            ...item,
            // Normalize field names for calendar compatibility
            task_name: item.task_name || item.name,
            equipment_name: item.equipment_name || 'Multiple Equipment',
            next_due_date: item.next_due_date,
            is_overdue: item.is_overdue || (item.days_until_due !== undefined && item.days_until_due < 0),
            is_maintenance_task: !!item.task_assignments_detail, // Flag to identify maintenance tasks vs schedules
          };
        }
        return acc;
      }, {});

      setDashboardData({
        ...statsResponse,
        // Update schedules to include maintenance tasks for calendar
        overdue_schedules: Object.values(uniqueItems).filter((item: any) => item.is_overdue),
        upcoming_schedules: Object.values(uniqueItems).filter((item: any) => !item.is_overdue),
      });

      // Transform the data into our expected format
      setDashboardStats({
        total_packages: tasksData.results?.length || statsResponse.total_schedules || 0,
        overdue_packages: tasksData.results?.filter((t: any) => t.is_overdue || t.has_overdue_assignments).length || 0,
        due_this_week: tasksData.results?.filter((t: any) => 
          t.days_until_due !== undefined && t.days_until_due <= 7 && t.days_until_due >= 0
        ).length || 0,
        in_progress_packages: statsResponse.active_work_orders?.length || 0,
        overdue_assignments: (statsResponse as any).overdue_assignments_count || 0,
        total_assignments: (statsResponse as any).total_assignments || 0,
        completion_rate: (statsResponse as any).completion_rate || 0,
        avg_completion_time: (statsResponse as any).avg_completion_time || 0,
      });
    } catch (err) {
      console.error('Failed to load dashboard stats:', err);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardStats();
  }, [currentSite.siteId]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadDashboardStats();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, currentSite.siteId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handlePackageComplete = (packageId: string) => {
    // Refresh stats when a package is completed
    loadDashboardStats();
  };

  const handleAssignmentComplete = (packageId: string, assignmentId: string) => {
    // Refresh stats when an assignment is completed
    loadDashboardStats();
  };

  const handleRefresh = () => {
    loadDashboardStats();
  };

  const handleSettingsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsMenuAnchor(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsMenuAnchor(null);
  };

  if (loading && currentTab === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Maintenance Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {currentSite.siteName} • Comprehensive maintenance management
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                size="small"
              />
            }
            label="Auto-refresh"
          />
          
          <Button
            startIcon={<Refresh />}
            onClick={handleRefresh}
            variant="outlined"
            size="small"
          >
            Refresh
          </Button>

          <IconButton onClick={handleSettingsMenuOpen}>
            <Settings />
          </IconButton>
          
          <Menu
            anchorEl={settingsMenuAnchor}
            open={Boolean(settingsMenuAnchor)}
            onClose={handleSettingsMenuClose}
          >
            <MenuItem onClick={() => {
              setShowOverdueOnly(!showOverdueOnly);
              handleSettingsMenuClose();
            }}>
              <FormControlLabel
                control={<Switch checked={showOverdueOnly} />}
                label="Show Overdue Only"
              />
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Warning color="error" sx={{ mr: 1 }} />
                <Typography variant="h4" color="error">
                  {dashboardStats.overdue_packages}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Overdue Packages
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dashboardStats.overdue_assignments} individual assignments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Schedule color="warning" sx={{ mr: 1 }} />
                <Typography variant="h4" color="warning.main">
                  {dashboardStats.due_this_week}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Due This Week
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Requires attention soon
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Engineering color="info" sx={{ mr: 1 }} />
                <Typography variant="h4" color="info.main">
                  {dashboardStats.in_progress_packages}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                In Progress
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Work orders active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp color="success" sx={{ mr: 1 }} />
                <Typography variant="h4" color="success.main">
                  {dashboardStats.completion_rate}%
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Completion Rate
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last 30 days performance
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="maintenance dashboard tabs">
            <Tab 
              icon={<Badge badgeContent={dashboardStats.overdue_packages} color="error"><ListAlt /></Badge>} 
              label="Package Workflow" 
            />
            <Tab 
              icon={<Badge badgeContent={dashboardStats.overdue_assignments} color="error"><PriorityHigh /></Badge>} 
              label="Overdue Tasks" 
            />
            <Tab 
              icon={<CalendarToday />} 
              label="Calendar View" 
            />
            <Tab 
              icon={<Dashboard />} 
              label="Analytics" 
            />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          {/* Main Package Workflow */}
          <Box sx={{ p: 3 }}>
            <MaintenancePackageWorkflow
              siteId={currentSite.siteId?.toString()}
              onPackageComplete={handlePackageComplete}
              onAssignmentComplete={handleAssignmentComplete}
              showOverdueOnly={showOverdueOnly}
              showDraftResumePrompts={true}
            />
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          {/* Hierarchical Overdue Task Manager */}
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Overdue Task Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Manage individual overdue tasks and assignments across all maintenance packages.
            </Typography>
            
            <HierarchicalOverdueTaskManager
              siteName={currentSite.siteName}
              onTaskComplete={handlePackageComplete}
              onAssignmentComplete={handleAssignmentComplete}
            />
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          {/* Calendar View */}
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Maintenance Calendar
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Calendar view of scheduled maintenance packages and deadlines.
            </Typography>
            
            <MaintenanceCalendar
              schedules={[...(dashboardData?.overdue_schedules || []), ...(dashboardData?.upcoming_schedules || [])]}
              workOrders={dashboardData?.active_work_orders || []}
              currentDate={calendarDate}
              onDateChange={setCalendarDate}
            />
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          {/* Analytics */}
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Maintenance Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Performance metrics and trends for maintenance operations.
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Completion Metrics
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                        <ListItemText 
                          primary="Completion Rate"
                          secondary={`${dashboardStats.completion_rate}% (last 30 days)`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><Schedule /></ListItemIcon>
                        <ListItemText 
                          primary="Average Completion Time"
                          secondary={`${dashboardStats.avg_completion_time} hours`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><Assignment /></ListItemIcon>
                        <ListItemText 
                          primary="Total Assignments"
                          secondary={`${dashboardStats.total_assignments} managed`}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      System Status
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon><Build color="primary" /></ListItemIcon>
                        <ListItemText 
                          primary="Active Packages"
                          secondary={`${dashboardStats.total_packages} packages configured`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><Engineering color="info" /></ListItemIcon>
                        <ListItemText 
                          primary="In Progress"
                          secondary={`${dashboardStats.in_progress_packages} work orders active`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><Warning color="warning" /></ListItemIcon>
                        <ListItemText 
                          primary="Attention Required"
                          secondary={`${dashboardStats.overdue_packages + dashboardStats.due_this_week} packages need attention`}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default EnhancedMaintenanceDashboard; 